import { Box, Button, ListItemButton, ListItemIcon, SvgIcon, ListItemText, Stack, CircularProgress } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router";
import { SvgIconComponent } from "@mui/icons-material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import IntegrationInstructionsRoundedIcon from "@mui/icons-material/IntegrationInstructionsRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { ApiType, apiTypeDocsMap, getApiTypeLabelMap, apiTypes } from ".";
import { ApiInfo } from "./ApiInfo";
import { useSelector } from "react-redux";
import { AppState } from "../../../../../../shell/store/types";
import { ApiDomainEndpoints } from "./ApiDomainEndpoints";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import { useGetContentModelQuery, useGetInstanceSettingsQuery } from "../../../../../../shell/services/instance";
import { HeadlessSwitcher } from "./HeadlessSwitcher";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
const apiTypeIconMap: Record<ApiType, SvgIconComponent> = {
  "quick-access": BoltRoundedIcon,
  "backend-coding": LockRoundedIcon,
  graphql: ViewInArRoundedIcon,
  "site-generators": IntegrationInstructionsRoundedIcon,
  "custom-endpoints": DashboardCustomizeRoundedIcon,
  "visual-layout": NewspaperRoundedIcon
};
const apiTypesWithEndpoints = ["quick-access", "backend-coding", "graphql", "site-generators"];
export const ApiDetails = () => {
  const {
    t
  } = useTranslation();
  const apiTypeLabelMap = getApiTypeLabelMap(t);
  const history = useHistory();
  const location = useLocation();
  const {
    contentModelZUID
  } = useParams<{
    contentModelZUID: string;
  }>();
  const {
    data: modelData
  } = useGetContentModelQuery(contentModelZUID, {
    skip: !contentModelZUID
  });
  const installedApps = useSelector((state: AppState) => state.apps.installed);
  const selectedType = location.pathname.split("/").pop() as ApiType;
  const {
    data: instanceSettings,
    isFetching
  } = useGetInstanceSettingsQuery(null, {
    skip: selectedType !== "site-generators"
  });
  const headlessEnabled = instanceSettings?.find(setting => setting.key === "mode")?.value !== "traditional";
  const filteredApiTypes = useMemo(() => {
    if (modelData?.type === "dataset") {
      return apiTypes.filter(apiType => apiType !== "site-generators");
    }
    return apiTypes;
  }, [modelData]);
  const handleVisualLayoutClick = () => {
    const layoutApp = installedApps?.find((app: any) => app?.name === "layouts");
    if (layoutApp) {
      history.push(`/app/${layoutApp.ZUID}`);
    } else {
      window.open("https://www.zesty.io/marketplace/apps/page-layout-designer");
    }
  };
  useEffect(() => {
    if (selectedType === "site-generators" && modelData?.type === "dataset") {
      history.replace(`${location.pathname.split("/").slice(0, -1).join("/")}`);
    }
  }, [selectedType, modelData]);
  return <Box display="flex" height="100%" sx={{
    overflowY: "auto"
  }} data-sentry-element="Box" data-sentry-component="ApiDetails" data-sentry-source-file="ApiDetails.tsx">
      <Box width="220px" pt={2} pl={4} sx={{
      borderRight: theme => `1px solid ${theme.palette.border}`
    }} data-sentry-element="Box" data-sentry-source-file="ApiDetails.tsx">
        <Box pb={1} data-sentry-element="Box" data-sentry-source-file="ApiDetails.tsx">
          <Button size="small" color="inherit" startIcon={<ArrowBackRoundedIcon color="action" />} onClick={() => history.push(`${location.pathname.split("/").slice(0, -1).join("/")}`)} data-sentry-element="Button" data-sentry-source-file="ApiDetails.tsx">
            {t("common.back")}
          </Button>
        </Box>
        <Box pr={1} data-sentry-element="Box" data-sentry-source-file="ApiDetails.tsx">
          {filteredApiTypes?.map(type => <ListItemButton key={type} sx={{
          py: "6px",
          px: "12px",
          borderRadius: "4px"
        }} selected={selectedType === type} onClick={() => history.push(`${location.pathname.split("/").slice(0, -1).join("/")}/${type}`)}>
              <ListItemIcon sx={{
            minWidth: "unset",
            mr: 1,
            color: selectedType === type && "primary.main"
          }}>
                <SvgIcon fontSize="small" component={apiTypeIconMap[type]} />
              </ListItemIcon>
              <ListItemText sx={{
            m: 0
          }} primary={apiTypeLabelMap[type]} primaryTypographyProps={{
            fontWeight: 500,
            variant: "caption",
            color: selectedType === type ? "primary.dark" : "text.secondary",
            sx: {
              wordBreak: "break-word"
            }
          }} />
            </ListItemButton>)}
        </Box>
      </Box>
      {isFetching ? <Box flex={1} display="flex" alignItems="center" justifyContent="center" height="100%">
          <CircularProgress />
        </Box> : <Box flex={1} pl={3} pr={4} py={2} sx={{
      overflow: "auto"
    }}>
          {selectedType === "site-generators" && !headlessEnabled ? <HeadlessSwitcher instanceSetting={instanceSettings?.find(setting => setting.key === "mode")} /> : <Box maxWidth="640px">
              <ApiInfo type={selectedType} large />

              {[...apiTypesWithEndpoints].includes(selectedType) && <Button variant="outlined" startIcon={<MenuBookRoundedIcon />} sx={{
          my: 3
        }} onClick={() => window.open(apiTypeDocsMap[selectedType])}>
                  {t("schema.readDocs")}
                </Button>}
              {selectedType === "custom-endpoints" && <Stack direction="row" gap={1.5} sx={{
          my: 3
        }}>
                  <Button variant="outlined" startIcon={<CodeRoundedIcon />} onClick={() => history.push("/code")}>
                    {t("schema.openCodeApp")}
                  </Button>
                  <Button variant="outlined" startIcon={<MenuBookRoundedIcon color="action" />} onClick={() => window.open(apiTypeDocsMap[selectedType])} color="inherit">
                    {t("schema.readDocs")}
                  </Button>
                </Stack>}
              {selectedType === "visual-layout" && <Stack direction="row" gap={1.5} sx={{
          my: 3
        }}>
                  <Button variant="outlined" startIcon={<NewspaperRoundedIcon />} onClick={handleVisualLayoutClick}>
                    {t("schema.openVisualLayout")}
                  </Button>
                  <Button variant="outlined" startIcon={<MenuBookRoundedIcon color="action" />} onClick={() => window.open(apiTypeDocsMap[selectedType])} color="inherit">
                    {t("schema.readDocs")}
                  </Button>
                </Stack>}
              {apiTypesWithEndpoints.includes(selectedType) && <ApiDomainEndpoints type={selectedType} />}
            </Box>}
        </Box>}
    </Box>;
};